import { request } from 'https';
import { TowState } from '../AppServices';
import { booleanToString } from '../utilities/Utilities';
import { AcceptBomResponsesRequest, AcceptBomResponsesResult, AcceptBomsContext, OfflineProcessDataResponseResult } from './Models/Api/AcceptBomsResponsesModels';
import { ApiDataResponse, ApiResponse, ResultType } from './Models/Api/ApiResponse';
import AppServicesResponse from './Models/Api/AppServicesResponse';
import { BulkReportRequest } from './Models/Api/BulkReportRequest';
import { CertificationConsolidationSurveyRequest, CertificationConsolidationSurveyResponse } from './Models/Api/CertificationConsolidationSurveyRequest';
import { ClientTeamRequestGetResult } from './Models/Api/Consultants/ClientTeamRequestGetResult';
import { CreateReportRequest, FavoriteAndPrivateListDefinitionsGetAllResponse, UpdateCustomerRequest } from './Models/Api/CustomerApiRequestsAndResponses';
import { AcceptOfflineAiGapSummaryResponseResult, DataCollectionGapSummaryPageResponse, SendGapReportEmailRequest } from './Models/Api/DataCollectionGapSummaryModels';
import GetStartDataResponse from './Models/Api/GetStartDataResponse';
import { InitializeCreateAndSendSurveysOfflineProcessRequest, InitializeCreateAndSendSurveysOfflineProcessResponse } from './Models/Api/InitializeCreateAndSendSurveysOfflineProcess';
import { InventoriesGetNamesResponse } from './Models/Api/InventoriesGetName';
import { InviteSuppliersBundleResponse } from './Models/Api/InviteSuppliersBundleResponse';
import SharedMaterialAccessRequestData from './Models/Api/SharedMaterialAccessRequestData';
import { GetCompanyFromCustomUrlNameResponse } from './Models/Api/SharedMaterials/GetCompanyFromCustomUrlName';
import SharedMaterialsAccessControlListInsertRequest from './Models/Api/SharedMaterialsAccessControlListInsertRequest';
import * as EmailRequests from './Models/Api/SharedMaterialsEmailRequests';
import { SharedMaterialsRemoveSharedMaterialsFromCatalogsRequest } from './Models/Api/SharedMaterialsRemoveSharedMaterialsFromCatalogsRequest';
import { SurveyEditorCreateNewSurveyRequestData } from './Models/Api/SurveyEditorCreateNewSurveyRequestModel';
import { CertificationConsolidationResponse, CertificationConsolidationUpdateResult } from './Models/Api/Surveys/CertificationConsolidationApiModels';
import { CreateSurveyCampaignRequest, SurveyCampaignTableEntry, SurveyCampaignTableResult, UpdateSurveyCampaignRequest } from './Models/Api/Surveys/SurveyCampaignModels';
import { ScipData, ScipSubmissionDataAll } from './Models/Api/Surveys/ScipData';
import { SubmitResponseToRequestorOfflineProcessResponse } from './Models/Api/Surveys/SubmitResponseToRequestorOfflineProcessResponse';
import SurveyTemplateManagerBundleResponse from './Models/Api/SurveyTemplateManagerBundleResponse';
import { TowDeleteRequest, TowUpdateRequest } from './Models/Api/TowRequests';
import UpdateCompanyRequest from './Models/Api/UpdateCompanyRequest';
import UploadFileResponse from './Models/Api/UploadFileResponse';
import { UserContextResponse } from './Models/Api/UserContextResponse';
import { Customer, CustomerRequest, MyCustomersBundle, Purchase } from './Models/Customers/Customer';
import { FileTableEntry } from './Models/Documents/DocumentModels';
import { FullInventory } from './Models/Inventories/FullInventory';
import { SimpleInventory, SimpleInventoryWithLinkedMaterialInfo } from './Models/Inventories/SimpleInventory';
import { BaseOfflineProcessData, CreateAndSendSurveysOfflineProcessData, OfflineGeneralSurveyProcessData } from './Models/OfflineProcesses/OfflineProcessModels';
import * as SharedMaterialsDataModel from './Models/SharedMaterials/DataModel';
import { InviteSupplierModel, Supplier } from './Models/Supplier/Supplier';
import * as SurveyDataModel from './Models/Surveys/SurveyDataModel';
import { OfflineResponseApiData, SurveyRequestResponseHistoryEntry, SupplierAiWorkflowRequest } from './Models/Surveys/SurveyDataModel';
import { AiSurveyOperation } from './StateControllers/Survey/SurveyResponse/SurveyResponseModels';
import { Task, TaskManagementApiRequest, TaskManagementApiResponse } from "./Models/TaskManagement/TaskManagementDataModel";
import { Ipc1752AXmlData } from './Models/XmlExport/XmlExportDataModel';
import { EngageConnectFormData } from './StateControllers/Engagement/EngagementStateController';
import { CoreSurveyResponseData, InventoryDetailsResponse, SurveyResponseValidationResult, WriteGroupedResponseInventoriesResult, ComplianceScoreResult, ExtensionRequestModel, PlausibilityData } from './StateControllers/Survey/SurveyResponse/SurveyResponseModels';
import { SharedMaterialsCatalogRequest, SharedMaterialsCatalogResult } from "./Models/Api/SharedMaterials/SharedMaterialsCatalogResult";
import { SharedMaterialPreviewDetailResponse } from "./Models/Api/SharedMaterials/SharedMaterialPreviewDetailResponse";
import { SharedMaterialEngageResponse } from "./Models/Api/SharedMaterials/SharedMaterialEngageResponse";
import { InventorySdsToFileNameMap } from './Models/Api/InventorySdsToFileNameMap';
import { ObtainmentReportsResult } from "./Models/Api/ObtainmentReports/ObtainmentReportsResult";
import { SftpConfigurationGetByOwnerResponse, SftpImportConfigurationSetStatusRequest, SftpImportConfiguration, SftpImportConfigurationWorkflow, convertWorkflowToBackend, convertBackendToWorkflow, InventoryColumnMapping } from './Models/Api/SftpImportModels';

// ===============================================================================

export interface InventoryComplianceScoresRequest {
    inventoryId: string;
    flagZerosAndMissingPercentagesOnSubstances: boolean;
}

export interface InventoryComplianceScoresResponse {
    result: string;
    complianceScoreResults: {
        [regulationId: string]: ComplianceScoreResult | null
    };
}

export interface LibraryFilterModel {
    type: string;
    arguments: string[];
    values: string[];
}

export interface LibraryFiltersModel {
    activeFilters: LibraryFilterModel[];
}

export interface LibraryInventoriesGetInventory {
    inventoryId: string;
    name: string;
    description: string;
    plantName: string;
    supplierId: string | null;
    supplierName: string;
    isComponent: boolean;
    folderId: string | null;
    tags: string[];
}

export interface LibraryInventoriesGetResult {
    result: string;
    totalNumberOfInventories: number;
    pageOfInventories: LibraryInventoriesGetInventory[];
}

//
// All public api methods return a result or a data result
//

export interface Result {
    success: boolean;
    result: ResultType | string;
    resultMessage?: string;
}

export interface DataResult<TData> extends Result {
    data: TData | null;
}

interface FetchSettings {
    method: "GET" | "POST" | "PUT"; // Default is GET
    body?: BodyInit;
    headers?: HeadersInit;
    timeoutInMilliseconds?: number; // defaults to 30 s
}

interface FetchResult {
    result: ResultType;
    httpResponse: Response | null;
}

declare const appInsights: any;

// ===============================================================================

export class ToxnotServiceClient {

    private static readonly supplierInventoryFetchTimeoutInMilliseconds = 10 * 60 * 1000;

    // ===============================================================================

    private async fetchImpl(endpoint: string, queryParameters: { [parameter: string]: string } | null, fetchSettings: FetchSettings = { method: "GET" }, prependApiVersion: boolean = true): Promise<FetchResult> {
        try {
            // Enable below code if you need to capture all the APIs with yser information
            //try {
            //    appInsights.trackEvent({
            //        name: endpoint,
            //        properties: queryParameters
            //    });
            //}
            //catch (e) {
            //    console.log('appinsight error - ' + e);
            //}
            const abortController = new AbortController();

            fetchSettings.timeoutInMilliseconds ??= 30000; // default 30 seconds

            const timeoutTimerId = setTimeout(() => abortController.abort(), fetchSettings.timeoutInMilliseconds);

            // send an http request to the endpoint and get back an http response

            let fullPath = prependApiVersion ? `/Api/v2/${endpoint}` : `/Api/${endpoint}`;

            if (queryParameters !== null) {
                const queryParameterKeyList = Object.keys(queryParameters);

                if (queryParameterKeyList.length > 0) {
                    const serializedParameters = queryParameterKeyList
                        .map(parameter => `${encodeURIComponent(parameter)}=${encodeURIComponent(queryParameters[parameter])}`)
                        .join("&");

                    fullPath += `?${serializedParameters}`;
                }
            }

            const httpResponse = await fetch(
                fullPath,
                {
                    method: fetchSettings.method,
                    signal: abortController.signal,
                    headers: fetchSettings.headers,
                    body: fetchSettings.body
                });

            clearTimeout(timeoutTimerId);

            //
            // Check server responses that indicate failure
            //

            if (!httpResponse.ok) {
                // Forbidden
                if (httpResponse.status === 403) {
                    window.location.href = "/Account/SignIn";

                    return {
                        result: ResultType.Forbidden,
                        httpResponse: httpResponse
                    };
                }
                // Not Found
                else if (httpResponse.status === 404) {
                    return {
                        result: ResultType.NotFound,
                        httpResponse: httpResponse
                    };
                }
                else if (httpResponse.status === 413) {
                    return {
                        result: ResultType.UploadTooLarge,
                        httpResponse: httpResponse
                    };
                }
                else {
                    console.log("SERVICE CLIENT ERROR: status=" + httpResponse.status + " textStatus=" + (httpResponse.statusText ?? "null"));

                    return {
                        result: ResultType.Failure,
                        httpResponse: httpResponse
                    };
                }
            }

            //
            // If we get here, we've got a successful response.
            //

            return {
                result: ResultType.Ok,
                httpResponse: httpResponse
            }
        }
        catch (error) {
            return {
                result: ResultType.Failure,
                httpResponse: null
            }
        }
    }

    // ===============================================================================

    private async getObjectFromResponseBody<T>(httpResponse: Response) {
        const bodyObject = await httpResponse.json() as T;

        return bodyObject;
    }

    // ===============================================================================

    private async fetchGet<TResponse>(endpoint: string, queryParameters?: { [parameter: string]: string } | null, timoutInMilliseconds?: number): Promise<DataResult<TResponse>> {
        const fetchSettings: FetchSettings = { method: "GET" };

        fetchSettings.timeoutInMilliseconds = timoutInMilliseconds ?? 30000;

        queryParameters ??= null;
        const { result, httpResponse } = await this.fetchImpl(endpoint, queryParameters, fetchSettings);

        if (result !== ResultType.Ok) {
            return {
                success: false,
                result: result,
                data: null
            };
        }

        if (httpResponse == null) {
            throw new Error("Unexpected null http response");
        }

        const apiResponse = await this.getObjectFromResponseBody<ApiDataResponse<TResponse>>(httpResponse);

        return {
            success: apiResponse.result === ResultType.Ok,
            result: apiResponse.result,
            resultMessage: apiResponse.resultMessage,
            data: apiResponse.data
        };
    }

    // ===============================================================================

    private getPostSettings<TRequest>(requestObject: TRequest | null, timeoutInMilliseconds?: number): FetchSettings {
        timeoutInMilliseconds ??= 30000; // default 30 seconds

        if (requestObject === null) {
            return {
                method: "POST",
                timeoutInMilliseconds: timeoutInMilliseconds
            };
        }

        const settings: FetchSettings = {
            method: "POST",
            body: JSON.stringify(requestObject),
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json'
            },
            timeoutInMilliseconds: timeoutInMilliseconds
        };

        return settings;
    }

    // ===============================================================================

    private async fetchPostWithNoDataResponse<TRequest>(
        endpoint: string,
        queryParameters: { [parameter: string]: string } | null,
        requestObject: TRequest | null = null,
        timeoutInMilliseconds?: number
    ): Promise<Result> {
        const settings = this.getPostSettings(requestObject, timeoutInMilliseconds);

        const { result, httpResponse } = await this.fetchImpl(endpoint, queryParameters, settings);
        if (result !== ResultType.Ok) {
            return {
                success: false,
                result: result
            };
        }

        if (httpResponse == null) {
            throw new Error("Unexpected null http response");
        }

        const apiResponse = await this.getObjectFromResponseBody<ApiResponse>(httpResponse);

        return {
            success: apiResponse.result === ResultType.Ok,
            result: apiResponse.result,
            resultMessage: apiResponse.resultMessage
        };
    }

    // ===============================================================================

    private async fetchPostForm(
        endpoint: string,
        formParameters: { [parameter: string]: string } | null,
        files: { [parameter: string]: File } | null,
        timeoutInMilliseconds?: number,
        prependApiVersion: boolean = true
    ) {
        const formData = new FormData();

        if (formParameters !== null) {
            const formKeys = Object.keys(formParameters);

            for (let formKey of formKeys) {
                formData.append(formKey, formParameters[formKey]);
            }
        }

        if (files !== null) {
            const fileKeys = Object.keys(files);

            for (let fileKey of fileKeys) {
                formData.append(fileKey, files[fileKey]);
            }
        }

        const settings: FetchSettings = {
            method: "POST",
            body: formData,
            timeoutInMilliseconds: timeoutInMilliseconds
        };

        return await this.fetchImpl(endpoint, null, settings, prependApiVersion);
    }

    // ===============================================================================

    private async fetchPutForm(
        endpoint: string,
        formParameters: { [parameter: string]: string } | null,
        timeoutInMilliseconds?: number
    ) {
        const formData = new FormData();

        if (formParameters !== null) {
            const formKeys = Object.keys(formParameters);

            for (let formKey of formKeys) {
                formData.append(formKey, formParameters[formKey]);
            }
        }

        const settings: FetchSettings = {
            method: "PUT",
            body: formData,
            timeoutInMilliseconds: timeoutInMilliseconds
        };

        return await this.fetchImpl(endpoint, null, settings);
    }

    // ===============================================================================

    private async fetchPostFormWithGenericResponse<TResponse>(
        endpoint: string,
        formParameters: { [parameter: string]: string } | null,
        files: { [parameter: string]: File } | null
    ): Promise<TResponse | null> {
        const { result, httpResponse } = await this.fetchPostForm(endpoint, formParameters, files);

        if (result !== ResultType.Ok) {
            return null;
        }

        if (httpResponse == null) {
            throw new Error("Unexpected null http response");
        }

        const apiResponse = await this.getObjectFromResponseBody<TResponse>(httpResponse);

        return apiResponse;
    }
    // ===============================================================================

    private async fetchPostFormWithDataResponse<TResponse>(
        endpoint: string,
        formParameters: { [parameter: string]: string } | null,
        files: { [parameter: string]: File } | null,
        timeoutInMilliseconds?: number,
        prependApiVersion: boolean = true
    ): Promise<DataResult<TResponse>> {
        const { result, httpResponse } = await this.fetchPostForm(endpoint, formParameters, files, timeoutInMilliseconds, prependApiVersion);

        if (result !== ResultType.Ok) {
            return {
                success: false,
                result: result,
                data: null
            };
        }

        if (httpResponse == null) {
            throw new Error("Unexpected null http response");
        }

        try {
            const apiResponse = await this.getObjectFromResponseBody<ApiDataResponse<TResponse>>(httpResponse);
            return {
                success: apiResponse.result === ResultType.Ok,
                result: apiResponse.result,
                resultMessage: apiResponse.resultMessage,
                data: apiResponse.data
            };
        } catch (error) {
            return {
                success: result === ResultType.Ok,
                result: result,
                data: null
            };
        }
    }

    // ===============================================================================

    private async fetchPutFormResponse<TResponse>(
        endpoint: string,
        formParameters: { [parameter: string]: string } | null,
        timeoutInMilliseconds?: number
    ): Promise<Result> {

        const { result, httpResponse } = await this.fetchPutForm(endpoint, formParameters, timeoutInMilliseconds);
        if (result !== ResultType.Ok) {
            return {
                success: false,
                result: result,
            };
        }

        if (httpResponse == null) {
            throw new Error("Unexpected null http response");
        }

        const apiResponse = await this.getObjectFromResponseBody<ApiResponse>(httpResponse);

        return {
            success: apiResponse.result === ResultType.Ok,
            result: apiResponse.result,
            resultMessage: apiResponse.resultMessage
        };
    }

    // ===============================================================================

    private async fetchPostFormWithNoDataResponse(
        endpoint: string,
        formParameters: { [parameter: string]: string } | null,
        files: { [parameter: string]: File } | null
    ): Promise<Result> {
        const { result, httpResponse } = await this.fetchPostForm(endpoint, formParameters, files);

        if (result !== ResultType.Ok) {
            return {
                success: false,
                result: result,
            };
        }

        return {
            success: true,
            result: result,
        };
    }

    // ===============================================================================

    private async fetchPostWithDataResponse<TRequest, TResponse>(endpoint: string, queryParameters: { [parameter: string]: string } | null, requestObject: TRequest, timeoutInMilliseconds?: number, prependApiVersion: boolean = true) {
        const settings = this.getPostSettings(requestObject, timeoutInMilliseconds);

        const { result, httpResponse } = await this.fetchImpl(endpoint, queryParameters, settings, prependApiVersion);

        if (result !== ResultType.Ok) {
            return {
                success: false,
                result: result,
                data: null
            };
        }

        if (httpResponse == null) {
            throw new Error("Unexpected null http response");
        }

        const apiResponse = await this.getObjectFromResponseBody<ApiDataResponse<TResponse>>(httpResponse);

        return {
            ...apiResponse,
            success: apiResponse.result === ResultType.Ok,
            result: apiResponse.result,
            resultMessage: apiResponse.resultMessage,
            data: apiResponse.data
        };
    }

    // ===============================================================================

    private async fetchPostWithResourceDataResponse<TRequest, TResponse>(endpoint: string, queryParameters: { [parameter: string]: string } | null, requestObject: TRequest, timeoutInMilliseconds?: number, prependApiVersion: boolean = true) {
        const settings = this.getPostSettings(requestObject, timeoutInMilliseconds);

        const { result, httpResponse } = await this.fetchImpl(endpoint, queryParameters, settings, prependApiVersion);

        if (result !== ResultType.Ok) {
            return {
                success: false,
                result: result,
                data: null
            };
        }

        if (httpResponse == null) {
            throw new Error("Unexpected null http response");
        }

        const resourceDataResult = await this.getObjectFromResponseBody<{ Result: string, ResultMessage: string, Data: TResponse }>(httpResponse);

        return {
            success: resourceDataResult.Result === ResultType.Ok,
            result: resourceDataResult.Result,
            resultMessage: resourceDataResult.ResultMessage,
            data: resourceDataResult.Data
        };
    }

    // ===============================================================================

    private async fetchPostWithResponse<TRequest, TResponse>(endpoint: string, queryParameters: { [parameter: string]: string } | null, requestObject: TRequest, timeoutInMilliseconds?: number, prependApiVersion: boolean = true) {
        const settings = this.getPostSettings(requestObject, timeoutInMilliseconds);

        const { result, httpResponse } = await this.fetchImpl(endpoint, queryParameters, settings, prependApiVersion);

        if (result !== ResultType.Ok) {
            return {
                success: false,
                result: result,
                data: null
            };
        }

        if (httpResponse == null) {
            throw new Error("Unexpected null http response");
        }

        const apiResponse = await this.getObjectFromResponseBody<TResponse>(httpResponse);

        return {
            success: true,
            result: ResultType.Ok,
            data: {
                ...apiResponse
            }
        };
    }

    // ===============================================================================

    public async getUserContext(): Promise<DataResult<UserContextResponse>> {
        return await this.fetchGet<UserContextResponse>("UserContext");
    }

    // ===============================================================================

    public async getAppServices(): Promise<DataResult<AppServicesResponse>> {
        return await this.fetchGet<AppServicesResponse>("AppServices");
    }

    // ===============================================================================

    public async getAllSharedMaterialsCoreData(request: SharedMaterialsCatalogRequest): Promise<DataResult<SharedMaterialsCatalogResult>> {
        return await this.fetchPostWithDataResponse<SharedMaterialsCatalogRequest, SharedMaterialsCatalogResult>("SharedMaterialsCatalog", null, request);
    }

    // ===============================================================================

    public async getAllSharedMaterialsCompaniesData(
        rowsPerPage: number = 50,
        pageIndex: number = 0,
        searchText: string,
        sortDirection: boolean = true,
    ): Promise<DataResult<any>> {
        let params: { [params: string]: string } = {
            "rowsPerPage": rowsPerPage.toString(),
            "pageIndex": pageIndex.toString(),
        };

        if (searchText !== null) {
            params["searchText"] = searchText.toString();
        }

        params["sortAscending"] = sortDirection ? "true" : "false";

        return await this.fetchGet<any>("SharedMaterialsCompanies", params);
    }

    // ===============================================================================

    public async getSharedMaterialDetail(sharedMaterialId: string, accessCode: string | null = null): Promise<DataResult<SharedMaterialsDataModel.SharedMaterialDetailResponse>> {
        const params: { [params: string]: string } = {
            "sharedMaterialId": sharedMaterialId,
        };

        if (accessCode !== null) {
            params["accessCode"] = accessCode
        }

        return await this.fetchGet<SharedMaterialsDataModel.SharedMaterialDetailResponse>(
            "SharedMaterialDetail",
            params
        );
    }

    // ===============================================================================

    public async updateSharedMaterialsCompany(request: UpdateCompanyRequest): Promise<DataResult<string>> {
        return await this.fetchPostWithDataResponse("SharedMaterialsCompanyUpdate", null, request);
    }

    // ===============================================================================

    public async offlineProcessDelete(request: string): Promise<DataResult<string>> {
        return await this.fetchGet("OfflineProcessDelete", { operationId: request });
    }

    // ===============================================================================

    public async sharedMaterialsCompaniesGet(): Promise<DataResult<SharedMaterialsDataModel.SharedMaterialsFullCompany[]>> {
        return await this.fetchGet("SharedMaterialsCompaniesGet");
    }

    // ===============================================================================

    public async sharedMaterialsCompaniesDelete(companyIds: string[]): Promise<Result> {
        return await this.fetchPostWithNoDataResponse("SharedMaterialsCompaniesDelete", null, { companyIds: companyIds });
    }

    // ===============================================================================

    public async getSharedMaterialPreview(publicInventoryId: string, publicInventoryDataId: string, inBulkPublishMode: boolean): Promise<DataResult<SharedMaterialPreviewDetailResponse>> {
        return await this.fetchGet<SharedMaterialPreviewDetailResponse>(
            "PreviewSM2",
            {
                "publicInventoryId": publicInventoryId,
                "publicInventoryDataId": publicInventoryDataId,
                "inBulkPublishMode": booleanToString(inBulkPublishMode)
            }
        );
    }

    // ===============================================================================

    public async getSharedMaterialEngage(companyName: string, productName: string): Promise<DataResult<SharedMaterialEngageResponse>> {
        return await this.fetchGet<SharedMaterialEngageResponse>(
            "Engage",
            {
                "companyName": companyName,
                "productName": productName
            }
        );
    }

    // ===============================================================================

    public async uploadGeneralImageFile(
        fileName: string,
        description: string,
        type: number,
        isSupplierUpload: boolean,
        isPublic: boolean,
        file: File
    ): Promise<DataResult<UploadFileResponse>> {
        return await this.fetchPostFormWithDataResponse<UploadFileResponse>(
            "GeneralFile",
            {
                "name": fileName,
                "description": description,
                "type": type.toString(),
                "isSupplierUpload": isSupplierUpload ? "True" : "False",
                "isPublic": isPublic ? "True" : "False"
            },
            {
                "fileData": file
            });
    }

    // ===============================================================================

    public async sharedMaterialsGetForTeam(): Promise<DataResult<{ [sharedMaterialId: string]: SharedMaterialsDataModel.SharedMaterialCoreData }>> {
        return await this.fetchGet("SharedMaterialsGetForTeam");
    }

    // ===============================================================================

    public async getSharedMaterialsCompany(ownerId: string, companyId: string): Promise<DataResult<SharedMaterialsDataModel.SharedMaterialsFullCompany>> {
        const params = {
            "ownerId": ownerId,
            "companyId": companyId
        };

        return this.fetchGet<SharedMaterialsDataModel.SharedMaterialsFullCompany>("SharedMaterialsCompanyGet", params);
    }

    // ===============================================================================

    public async getSharedMaterialsCompanyFirst(ownerId: string): Promise<DataResult<SharedMaterialsDataModel.SharedMaterialsFullCompany>> {
        const params = {
            "ownerId": ownerId,
        };

        return this.fetchGet<SharedMaterialsDataModel.SharedMaterialsFullCompany>("SharedMaterialsCompanyGetFirst", params);
    }

    // ===============================================================================

    public async contactCompany(request: EmailRequests.ContactACompanyRequest) {
        return await this.fetchPostWithNoDataResponse("SharedMaterialsContactACompany", null, request);
    }

    // ===============================================================================

    public async shareMaterialByEmail(request: EmailRequests.ShareAMaterialRequest): Promise<Result> {
        return await this.fetchPostWithNoDataResponse("SharedMaterialsShareAMaterial", null, request);
    }

    public async sharedMaterialsGetByIds(sharedMaterialIds: string[]): Promise<DataResult<SharedMaterialsDataModel.SharedMaterialIdToCoreDataTable>> {
        return await this.fetchPostWithDataResponse("SharedMaterialsGetByIds", null, sharedMaterialIds);
    }

    // ===============================================================================

    public async requestAccessPermission(accessRequestData: SharedMaterialAccessRequestData) {
        return await this.fetchPostWithNoDataResponse("SharedMaterialsAccessRequestInsert", null, accessRequestData);
    }

    // ===============================================================================

    //
    // Shared Materials Access Requests and Control
    //

    public async getSharedMaterialAccessRequests(): Promise<DataResult<SharedMaterialsDataModel.SharedMaterialsAccessRequestCatalog>> {
        return await this.fetchGet("SharedMaterialsAccessRequestGetAll");
    }

    // ===============================================================================

    public async respondToSharedMaterialAccessRequest(requestId: string, responseType: SharedMaterialsDataModel.AccessRequestResponseType): Promise<Result> {
        return await this.fetchPostWithNoDataResponse("SharedMaterialsAccessRequestRespond", { "requestId": requestId, "type": responseType.toString() }, request);
    }

    // ===============================================================================

    public async getCompanyHasPublishedSharedMaterials(): Promise<DataResult<boolean>> {
        return await this.fetchGet("SharedMaterialCompanyHasPublishedMaterials");
    }

    // ===============================================================================

    public async sharedMaterialsGetAccessManagerBundle(): Promise<DataResult<SharedMaterialsDataModel.AccessManagerBundle>> {
        return await this.fetchGet("SharedMaterialsGetAccessManagerBundle");
    }

    // ===============================================================================

    public async batchSurveyResponseBomImport(responseId: string, file: File): Promise<SharedMaterialsDataModel.UploadResult | null> {
        return await this.fetchPostFormWithGenericResponse("BatchSurveyResponseBomImport", { "responseId": responseId }, { "spreadsheetFile": file },);
    }

    // ===============================================================================

    public async importDataGet(importId: string, retrurnResults: string): Promise<SharedMaterialsDataModel.OfflineResult | null> {
        return await this.fetchPostFormWithGenericResponse("ImportDataGet", { "importId": importId, "retrurnResults": retrurnResults }, null);
    }

    // ===============================================================================

    public async fetchCmrt(file: File) {
        return await this.fetchPostFormWithGenericResponse("FetchCmrt", null, { "fileData": file });
    }

    public async fetchCmrtV2(file: File) {
        return await this.fetchPostFormWithGenericResponse("FetchCmrtV2", null, { "fileData": file });
    }

    // ===============================================================================

    public async FetchCountries() {
        return await this.fetchPostFormWithGenericResponse("FetchCountries", null, null);
    }

    public async FetchSmelters() {
        return await this.fetchPostFormWithGenericResponse("FetchSmelters", null, null);
    }

    // ===============================================================================

    public async enqueueImport(importId: string): Promise<Result> {
        return await this.fetchPostFormWithNoDataResponse("EnqueueImport", { "importId": importId }, null);
    }

    // ===============================================================================

    public async removeFromAccessControlList(teamOwnerIdWithAccess: string, sharedMaterialId: string): Promise<Result> {
        return await this.fetchPostWithNoDataResponse(
            "SharedMaterialsAccessControlListDelete",
            {
                "teamOwnerIdWithAccess": teamOwnerIdWithAccess,
                "sharedMaterialId": sharedMaterialId
            },
            null);
    }

    // ===============================================================================

    public async grantGroupAccess(insertRequest: SharedMaterialsAccessControlListInsertRequest): Promise<Result> {
        return await this.fetchPostWithNoDataResponse("SharedMaterialsAccessControlListInsert", null, insertRequest);
    }

    // ===============================================================================

    public async sharedMaterialsUpdateFetchForCatalogs(request: SharedMaterialsRemoveSharedMaterialsFromCatalogsRequest): Promise<Result> {
        return await this.fetchPostWithNoDataResponse("SharedMaterialsUpdateFetchForCatalogs", null, request);
    }

    // ===============================================================================

    //
    // TOW
    //

    public async getActiveTows(): Promise<DataResult<TowState[]>> {
        return await this.fetchGet<TowState[]>("ActiveTows");
    }

    // ===============================================================================

    public async towUpdate(request: TowUpdateRequest): Promise<Result> {
        return await this.fetchPostWithNoDataResponse("TowUpdate", null, request);
    }

    // ===============================================================================

    public async towDelete(request: TowDeleteRequest): Promise<Result> {
        return await this.fetchPostWithNoDataResponse("TowDelete", null, request);
    }

    // ===============================================================================

    public async tryRedirectToMainApp(url: string): Promise<Result> {
        return await this.fetchPostWithNoDataResponse("TryRedirectToMainApp", { "url": url }, null);
    }

    //================================================================================

    public async getDataCollectionRequest(requestId: string, timeoutInMilliseconds?: number): Promise<DataResult<SurveyDataModel.SurveyRequestApiResponseBundle>> {
        return await this.fetchPostFormWithDataResponse("GetDataCollectionRequest", { "requestId": requestId, "version": "2" }, null, timeoutInMilliseconds);
    }

    //================================================================================

    public async createDataCollectionRequest(model: SurveyEditorCreateNewSurveyRequestData): Promise<DataResult<string>> {
        return await this.fetchPostWithDataResponse<SurveyEditorCreateNewSurveyRequestData, string>("CreateDataCollectionRequests", null, model,
            10 * 60 * 1000); // 10 minute timeout
    }

    //================================================================================

    public async sendDataCollectionRequestDraft(requestId: string): Promise<DataResult<string>> {
        return await this.fetchPostWithDataResponse<string, string>(
            "SendDataCollectionRequestDraft",
            { "requestId": requestId, "version": "2" },
            "", //requestObject, string cannot be null but we don't need a requestobject
            10 * 60 * 1000); // 10 minute timeout
    }

    //================================================================================

    public async surveyRequestUpdate(model: SurveyDataModel.SurveyRequest): Promise<Result> {
        return await this.fetchPostWithNoDataResponse("DataCollectionRequestUpdate", null, model,
            10 * 60 * 1000); // 10 minute timeout
    }

    //================================================================================

    public async surveySetAsNoValidationRequired(requestId: string): Promise<Result> {
        return await this.fetchPostWithNoDataResponse("SurveySetAsNoValidationRequired", { "requestId": requestId, "version": "2" }, null);
    }

    //================================================================================

    public async sendRequestReminderEmails(requestIds: Readonly<string[]>, reminderMessage: string): Promise<Result> {
        return await this.fetchPostWithNoDataResponse("SendRequestReminderEmails", null, { "requestIds": requestIds, "reminderMessage": reminderMessage });
    }

    //=================================================================================

    public async getDataCollectionTemplatesAll(): Promise<DataResult<SurveyDataModel.SurveyTemplateDataModel[]>> {
        return await this.fetchPostFormWithDataResponse("DataCollectionTemplateGetAll", null, null);
    }

    //=================================================================================

    public async deleteDataCollectionTemplate(templateId: string): Promise<Result> {
        return await this.fetchPostWithNoDataResponse("DataCollectionTemplateDelete", { "templateId": templateId }, null)
    }

    //=================================================================================

    public async dataCollectionResponseImportInventory(responseId: string | null, responseInventoryId: string | null, sourceInventoryId: string): Promise<Result> {
        return await this.fetchPostWithNoDataResponse("DataCollectionResponseImportInventory", { "responseId": responseId ?? "", "responseInventoryId": responseInventoryId ?? "", "sourceInventoryId": sourceInventoryId, }, null)
    }

    //=================================================================================

    public async updateDataCollectionTemplate(model: SurveyDataModel.SurveyTemplateDataModel): Promise<DataResult<string>> {
        return await this.fetchPostWithDataResponse<SurveyDataModel.SurveyTemplateDataModel, string>("DataCollectionTemplateUpdate", null, model);
    }

    //=================================================================================

    public async getDataCollectionMetadata(inventoryIdsInRequest: string[]): Promise<DataResult<SurveyDataModel.SurveyRequestApiResponseBundle>> {
        return await this.fetchPostWithDataResponse("GetDataCollectionMetadata", null, { inventoryIdsInRequest: inventoryIdsInRequest });
    }

    //=================================================================================

    public async passportTemplateGet(sharedMaterialId: string, accessCode: string | null): Promise<DataResult<SurveyDataModel.SurveyTemplateDataModel>> {
        return await this.fetchGet("PassportTemplateGet", { sharedMaterialId: sharedMaterialId, accessCode: accessCode ?? "" });
    }

    //=================================================================================

    public async passportTemplatePost(sharedMaterialId: string, accessCode: string | null, data: SurveyEditorCreateNewSurveyRequestData): Promise<ApiDataResponse<string>> {
        return await this.fetchPostWithDataResponse("PassportTemplatePost", { sharedMaterialId: sharedMaterialId, accessCode: accessCode ?? "" }, data);
    }

    // ===============================================================================

    public async addSharedMaterialToLibrary(sharedMaterialId: string): Promise<DataResult<string>> {
        return await this.fetchPostWithDataResponse<null, string>("SharedMaterialCopyToUserLibraryInventory",
            {
                "sharedMaterialId": sharedMaterialId,
            },
            null
        );
    }

    //=================================================================================

    public async exportSurveyRequest(surveyId: string): Promise<DataResult<string>> {
        return await this.fetchPostWithDataResponse<string, string>("ExportSurveyRequest", null, surveyId);
    }

    //=================================================================================

    public async initializeSurveyAiAuditExportProcess(
        requestId: string
    ): Promise<DataResult<{ operationId: string }>> {
        return await this.fetchPostWithDataResponse<{ requestId: string }, { operationId: string }>(
            "InitializeSurveyAiAuditExportProcess",
            null,
            {
                requestId
            }
        );
    }

    //=================================================================================

    public async getAISurveyOperationsForRequest(requestId: string): Promise<DataResult<AiSurveyOperation[]>> {
        return await this.fetchGet<AiSurveyOperation[]>(
            "GetAISurveyOperationsForRequest",
            { requestId }
        );
    }

    //=================================================================================

    public async getDataCollectionGapSummary(
        requestId: string,
        operationId: string
    ): Promise<DataResult<DataCollectionGapSummaryPageResponse>> {
        return await this.fetchGet<DataCollectionGapSummaryPageResponse>(
            "GetDataCollectionGapSummary",
            {
                requestId,
                operationId
            }
        );
    }

    //=================================================================================

    public async acceptOfflineAiGapSummaryResponse(requestId: string): Promise<DataResult<AcceptOfflineAiGapSummaryResponseResult>> {
        return await this.fetchPostWithDataResponse<null, AcceptOfflineAiGapSummaryResponseResult>(
            "AcceptOfflineAiGapSummaryResponse",
            { requestId },
            null,
            60000
        );
    }

    //=================================================================================

    public getDataCollectionGapReportDownloadUrl(requestId: string, operationId: string): string {
        return "/Api/DownloadDataCollectionGapReport?requestId=" + encodeURIComponent(requestId)
            + "&operationId=" + encodeURIComponent(operationId);
    }

    //=================================================================================

    public async sendDataCollectionGapReportEmail(request: SendGapReportEmailRequest): Promise<Result> {
        return await this.fetchPostWithNoDataResponse<SendGapReportEmailRequest>(
            "SendDataCollectionGapReportEmail",
            null,
            request,
            60000
        );
    }

    //=================================================================================

    public async triggerSupplierAiWorkflow(request: SupplierAiWorkflowRequest): Promise<DataResult<string>> {
        return await this.fetchPostWithResourceDataResponse("TriggerSupplierAiWorkflow", null, request);
    }

    public async retriggerSupplierAiWorkflow(requestId: string, operationId: string): Promise<DataResult<string>> {
        const queryParams: { [key: string]: string } = {
            "requestId": requestId,
            "operationId": operationId
        };

        return await this.fetchPostWithResourceDataResponse("RetriggerSupplierAiWorkflow", queryParams, null);
    }

    public async getAISurveyOperationStatus(requestId: string, operationId: string): Promise<DataResult<AiSurveyOperation>> {
        const queryParams: { [key: string]: string } = {
            "requestId": requestId,
            "operationId": operationId
        };
        return await this.fetchGet<AiSurveyOperation>("GetAISurveyOperationStatus", queryParams);
    }

    public async startDeleteAllAiAnswersWorkflow(request: SurveyDataModel.AiDeleteAllAnswersStartRequest): Promise<DataResult<string>> {
        return await this.fetchPostWithResourceDataResponse("StartDeleteAllAiAnswersWorkflowV2", null, request);
    }

    public async terminateSupplierAiWorkflow(request: SurveyDataModel.AiTerminateRequest): Promise<DataResult<string>> {
        return await this.fetchPostWithResourceDataResponse("TerminateSupplierAiWorkflow", null, request);
    }

    public async aiQuestionHelpfulFeedback(request: SurveyDataModel.AiQuestionHelpfulFeedbackRequest): Promise<Result> {
        return await this.fetchPostWithNoDataResponse("AiQuestionHelpfulFeedback", null, request);
    }

    public async aiBomHelpfulFeedback(request: SurveyDataModel.AiBomHelpfulFeedbackRequest): Promise<Result> {
        return await this.fetchPostWithNoDataResponse("AiBomHelpfulFeedback", null, request);
    }

    public async getAiBomHelpfulFeedback(responseId: string, inventoryId: string): Promise<DataResult<SurveyDataModel.AiBomHelpfulFeedbackResponse>> {
        return await this.fetchGet<SurveyDataModel.AiBomHelpfulFeedbackResponse>(
            "GetAiBomHelpfulFeedback",
            { responseId, inventoryId }
        );
    }

    //=================================================================================

    public async search(query: string, pageNum: string): Promise<SharedMaterialsDataModel.SubstanceResult | null> {
        return await this.fetchPostFormWithGenericResponse("Search", { "query": query, "pageNum": pageNum }, null);
    }

    //===============================================================================

    public async sharedMaterialsSearchSimilar(materialNames: string, supplierName: string): Promise<DataResult<SharedMaterialsDataModel.SharedMaterialsCatalogData[]>> {
        return await this.fetchPostWithDataResponse<null, SharedMaterialsDataModel.SharedMaterialsCatalogData[]>("SharedMaterialsSearchSimilar", { "materialNames": materialNames, "supplierNames": supplierName }, null);
    }

    // ==============================================================================

    public async copySharedMaterialDataIntoLibraryInventory(sharedMaterialId: string, targetInventoryId: string): Promise<Result> {
        return await this.fetchPostWithNoDataResponse("CopySharedMaterialDataIntoInventory", { "sharedMaterialId": sharedMaterialId, "targetInventoryId": targetInventoryId }, null);
    }

    // ==============================================================================

    public async surveyAcceptBomResponses(requestId: string, acceptBomsContext: AcceptBomsContext, saveCopies: boolean): Promise<DataResult<BaseOfflineProcessData<any, OfflineProcessDataResponseResult>>> {
        const request: AcceptBomResponsesRequest = {
            requestId: requestId,
            acceptBomsContext: acceptBomsContext,
            saveCopies: saveCopies
        };

        return await this.fetchPostWithDataResponse<AcceptBomResponsesRequest, BaseOfflineProcessData<any, OfflineProcessDataResponseResult>>("AcceptDataCollectionBomResponses", null, request, 60000);
    }

    // ==============================================================================

    public async surveyAcceptSupplierLevelResponse(requestId: string, historyId: string): Promise<DataResult<AcceptBomResponsesResult>> {
        return await this.fetchPostFormWithDataResponse<AcceptBomResponsesResult>(
            "AcceptDataCollectionSupplierResponse",
            {
                "requestId": requestId,
                "historyId": historyId
            },
            null);
    }

    // ==============================================================================

    public async surveyRequestAddOfflineResponse(data: OfflineResponseApiData): Promise<DataResult<SurveyRequestResponseHistoryEntry>> {
        return await this.fetchPostWithDataResponse("SurveyRequestAddOfflineResponseHistoryEntry", null, data);
    }

    // ==============================================================================

    public async surveySendGeneralRevisionRequest(requestId: string, revisionInstructions: string): Promise<Result> {
        const request = {
            requestId: requestId,
            historyId: null,
            generalRevisionRequest: {
                generalRevisionInstructions: revisionInstructions
            }
        };

        return await this.fetchPostWithNoDataResponse("SendRevisionRequest", null, request, 10 * 60 * 1000);
    }

    // ==============================================================================

    public async surveySendGranularRevisionRequest(requestId: string, historyEntryId: string, granularRevisionRequest: SurveyDataModel.SurveyHistoryEntryGranularRevisionRequest): Promise<Result> {
        const request = {
            requestId: requestId,
            historyId: historyEntryId,
            granularRevisionRequest: granularRevisionRequest
        };

        return await this.fetchPostWithNoDataResponse("SendRevisionRequest", null, request, 10 * 60 * 1000);
    }

    // ==============================================================================

    public async uploadGeneralFile(
        fileName: string,
        description: string,
        type: number,
        isSupplierUpload: boolean,
        isPublic: boolean,
        file: File,
        metadataType?: number,
        inventoryId?: string
    ): Promise<DataResult<UploadFileResponse>> {
        return await this.fetchPostFormWithDataResponse<UploadFileResponse>(
            "GeneralFile",
            {
                "name": fileName,
                "description": description,
                "type": type.toString(),
                "inventoryId": inventoryId ?? "",
                "metadataType": metadataType ? metadataType.toString() : "",
                "isSupplierUpload": isSupplierUpload ? "True" : "False",
                "isPublic": isPublic ? "True" : "False"
            },
            {
                "fileData": file
            });
    }

    // ==============================================================================

    public async updateInventoryDetails(
        responseId: string,
        inventoryId: string,
        name: string,
        description: string,
        fileId: string
    ): Promise<Result> {
        return await this.fetchPutFormResponse("UpdateInventoryDetails", {
            "responseId": responseId,
            "inventoryId": inventoryId,
            "name": name,
            "description": description,
            "photoFileId": fileId
        });
    }

    // ==============================================================================

    public async addInventory(
        responseId: string,
        inventoryName: string,
    ): Promise<DataResult<string>> {
        return await this.fetchPostWithDataResponse<any, string>("AddInventory", {
            "responseId": responseId,
            "inventoryName": inventoryName
        }, {});
    }

    // ==============================================================================

    public async deleteInventory(
        responseId: string,
        inventoryId: string,
    ): Promise<DataResult<string>> {
        return await this.fetchPostWithDataResponse<any, string>("deleteInventory", {
            "responseId": responseId,
            "inventoryId": inventoryId
        }, null);
    }

    // ==============================================================================

    public async dataCollectionResponseWriteGroupedResponseInventories(
        responseId: string,
        groupedResponseInventories: { [name: string]: SurveyDataModel.SurveyResponseInventoryGroup }
    ): Promise<DataResult<WriteGroupedResponseInventoriesResult>> {
        return await this.fetchPostWithDataResponse(
            "DataCollectionResponseWriteGroupedResponseInventories",
            null,
            {
                responseId: responseId,
                groupedResponseInventories: groupedResponseInventories
            },
            1000 * 60 * 15);
    }

    // ==============================================================================

    public async uploadInventoryFile(
        fileName: string,
        description: string,
        type: number,
        isSupplierUpload: boolean,
        isPublic: boolean,
        file: File,
        metadataType?: number,
        inventoryId?: string,
        isDelete?: boolean,
        fileId?: string
    ): Promise<DataResult<UploadFileResponse>> {
        return await this.fetchPostFormWithDataResponse<UploadFileResponse>(
            "InventoryFile",
            {
                "name": fileName,
                "description": description,
                "type": type.toString(),
                "inventoryId": inventoryId ?? "",
                "metadataType": metadataType ? metadataType.toString() : "",
                "isSupplierUpload": isSupplierUpload ? "True" : "False",
                "isPublic": isPublic ? "True" : "False",
                "version": "2",
                "delete": isDelete ? "True" : "False",
                "fileId": fileId ?? ""
            },
            {
                "fileData": file
            });
    }

    // ==============================================================================

    public async deleteGeneralFile(fileId: string): Promise<DataResult<UploadFileResponse>> {
        return await this.fetchPostFormWithDataResponse<UploadFileResponse>(
            "GeneralFile",
            {
                "fileId": fileId,
                "delete": "True"
            }, null);
    };

    public async deleteInventoryFile(fileId: string, inventoryId: string): Promise<DataResult<UploadFileResponse>> {
        return await this.fetchPostFormWithDataResponse<UploadFileResponse>(
            "InventoryFile",
            {
                "fileId": fileId,
                "delete": "True",
                "inventoryId": inventoryId
            }, null);
    };

    // ==============================================================================

    public async getSourceInventoryIdForPublicInventoryId(publicInventoryId: string): Promise<DataResult<string>> {
        return await this.fetchPostWithDataResponse<null, string>("GetSourceInventoryIdForPublicInventoryId",
            {
                "publicInventoryId": publicInventoryId,
            },
            null
        );
    }

    public async importSdsIngredientsIntoBom(inventoryId: string, sdsId: string, ingredientsData: string, overWriteBomRows: boolean = true): Promise<DataResult<string>> {
        return await this.fetchPostWithDataResponse("ImportSdsIngredientsIntoBom",
            {
                "inventoryId": inventoryId,
                "sdsId": sdsId,
                "ingredientsData": ingredientsData,
                "overWriteBomRows": overWriteBomRows.toString()
            },
            null
        );
    }

    public async inventoryGetAllSdsWithFile(inventoryId: string): Promise<DataResult<InventorySdsToFileNameMap[]>> {
        return await this.fetchPostWithDataResponse<null, InventorySdsToFileNameMap[]>("InventoryGetAllSdsWithFile",
            {
                "inventoryId": inventoryId,
            },
            null
        );
    }

    // ===============================================================================

    public async engageConnectWithAssaAbloy(request: EngageConnectFormData): Promise<Result> {
        return await this.fetchPostWithNoDataResponse("EngageConnectWithAssaAbloy", null, request);
    }

    // ===============================================================================

    public async getStartData(): Promise<DataResult<GetStartDataResponse>> {
        return await this.fetchGet<GetStartDataResponse>("GetStartData");
    }

    // ===============================================================================

    public async importSuppliersFromFile(file: File): Promise<DataResult<InviteSupplierModel[]>> {
        return await this.fetchPostFormWithDataResponse<InviteSupplierModel[]>(
            "InviteSuppliersImport",
            null,
            {
                "fileData": file
            }
        );
    }

    // ===============================================================================

    public async suppliersGet(): Promise<DataResult<Supplier[]>> {
        return await this.fetchGet("SuppliersGet", null);
    }

    // ===============================================================================

    public async inviteSuppliersBundleGet(): Promise<DataResult<InviteSuppliersBundleResponse>> {
        return await this.fetchGet("InviteSuppliersBundleGet");
    }

    // ===============================================================================

    public async initializeCreateAndSendSurveysOfflineProcess(request: InitializeCreateAndSendSurveysOfflineProcessRequest): Promise<DataResult<InitializeCreateAndSendSurveysOfflineProcessResponse>> {
        return await this.fetchPostWithDataResponse(
            "InitializeCreateAndSendSurveysOfflineProcess",
            null,
            request
        );
    }

    // ===============================================================================

    public async initializeOneClickSendSurveysOfflineProcess(selectedInventoryIds: string[], templateId: string, campaignId: string | null = null): Promise<DataResult<{ operationId: string }>> {
        return await this.fetchPostWithDataResponse(
            "InitializeOneClickSendSurveysOfflineProcess",
            null,
            {
                templateId: templateId,
                campaignId: campaignId,
                selectedInventoryIds: selectedInventoryIds
            });
    }

    // ===============================================================================

    public async createAndSendSurveysOfflineProcessDataGet(operationId: string, returnResults: boolean): Promise<DataResult<CreateAndSendSurveysOfflineProcessData>> {
        return await this.fetchGet<CreateAndSendSurveysOfflineProcessData>(
            "CreateAndSendSurveysOfflineProcessDataGet",
            {
                "operationId": operationId,
                "returnResults": returnResults.toString()
            }
        );
    }


    // ===============================================================================

    public async offlineProcessGet(operationId: string, requestContext: boolean) {
        return await this.fetchGet<OfflineGeneralSurveyProcessData>(
            "OfflineProcessGet",
            {
                "operationId": operationId,
                "requestContext": requestContext ? "True" : "False"
            }
        );
    };

    // ===============================================================================

    public async inventoriesCountLibraryBoms(): Promise<DataResult<number>> {
        return await this.fetchGet("InventoriesCountLibraryBoms");
    }

    // ===============================================================================

    public async getIpc1752aData(inventoryId: string, refresh: boolean): Promise<DataResult<Ipc1752AXmlData>> {
        return await this.fetchPostWithDataResponse("GetIpc1752aPreviewData", { "inventoryId": inventoryId, "refresh": refresh ? "True" : "False" }, null, 2 * 60 * 1000);
    }

    // ===============================================================================

    public async setIpc1752aData(ipc1752aData: Ipc1752AXmlData, inventoryId: string) {
        const request = {
            ipc1752aDataJson: ipc1752aData,
            inventoryId: inventoryId
        }

        return await this.fetchPostWithNoDataResponse("SetIpc1752aData", null, request);
    }

    // ===============================================================================

    public async updateCustomer(customer: UpdateCustomerRequest): Promise<DataResult<Customer>> {
        return await this.fetchPostWithDataResponse(
            "CustomerUpdate",
            null,
            customer
        )
    }

    // ===============================================================================

    public async getCustomersBundle(): Promise<DataResult<MyCustomersBundle>> {
        return await this.fetchGet("CustomersGetBundle");
    }

    // ===============================================================================

    public async deleteCustomers(customerIds: string[]): Promise<Result> {
        return await this.fetchPostWithNoDataResponse("CustomersDelete", null, customerIds);
    }

    // ===============================================================================

    public async uploadCustomers(file: File): Promise<Result> {
        return await this.fetchPostFormWithNoDataResponse(
            "UploadCustomers",
            null,
            {
                "fileData": file
            }
        );
    }

    // ===============================================================================

    public async checkIfCompanyCustomUrlIsValid(companyId: string | null, customName: string): Promise<DataResult<boolean>> {
        const params: { [param: string]: string } = {};
        params["customUrlName"] = customName;

        if (companyId !== null) {
            params["companyId"] = companyId;
        }

        return await this.fetchGet("CompanyCustomUrlNameIsValid", params);
    }

    // ===============================================================================

    public async getCompanyFromCustomUrlName(customUrlName: string): Promise<DataResult<GetCompanyFromCustomUrlNameResponse>> {
        return await this.fetchGet("GetCompanyFromCustomUrlName", { "customUrlName": customUrlName });
    }

    // ===============================================================================

    public async createCustomerReportRequest(request: CreateReportRequest): Promise<DataResult<CustomerRequest>> {
        return await this.fetchPostWithDataResponse("CreateCustomerReportRequest", null, request);
    }

    // ===============================================================================

    public async updateCustomerRequest(request: CustomerRequest): Promise<Result> {
        return await this.fetchPostWithNoDataResponse("CustomerRequestUpdate", null, request);
    }

    // ===============================================================================

    public async respondToCustomerRequests(requestIds: string[]): Promise<DataResult<string>> {
        return await this.fetchPostWithDataResponse("RespondToCustomerRequests", null, { customerRequestIds: requestIds });
    }

    // ==============================================================================

    public async getInventoriesNames(inventoryIds: string[]): Promise<DataResult<InventoriesGetNamesResponse[]>> {
        return await this.fetchPostWithDataResponse<{ inventoryIds: string[] }, InventoriesGetNamesResponse[]>("InventoriesGetNames", null, { inventoryIds: inventoryIds });
    }

    // ===============================================================================

    public async initializeBulkReporting(request: BulkReportRequest): Promise<DataResult<string>> {
        return await this.fetchPostWithDataResponse("InitializeBulkReporting", null, request);
    }

    // ===============================================================================

    public async favoriteAndPrivateListDefinitionsGetAll(): Promise<DataResult<FavoriteAndPrivateListDefinitionsGetAllResponse>> {
        return await this.fetchGet("FavoriteAndPrivateListDefinitionsGetAll");
    }

    // ===============================================================================

    public async offlineProcessDataGet<TContext, TResults>(operationId: string, requestContext: boolean): Promise<DataResult<BaseOfflineProcessData<TContext, TResults>>> {
        return await this.fetchGet("OfflineProcessGet", { "operationId": operationId, "requestContext": requestContext ? "True" : "False" });
    }

    // ===============================================================================

    public async customerRequestGet(requestId: string): Promise<DataResult<CustomerRequest>> {
        return await this.fetchGet("CustomerRequestGet", { "requestId": requestId });
    }

    // ===============================================================================

    public async customerInventoryPurchasesUpdate(customerId: string, inventoryIds: string[]): Promise<DataResult<Purchase[]>> {
        return await this.fetchPostWithDataResponse("CustomerInventoryPurchasesUpdate", { "customerId": customerId }, inventoryIds);
    }

    // ===============================================================================

    public async customerInventoryPurchasesGetAllForCustomer(customerId: string): Promise<DataResult<Purchase[]>> {
        return await this.fetchGet("CustomerInventoryPurchasesGetAllForCustomer", { "customerId": customerId });
    }

    // ===============================================================================

    public async customerInventoryPurchasesDelete(customerId: string, inventoryIds: string[]): Promise<Result> {
        return await this.fetchPostWithNoDataResponse("CustomerInventoryPurchasesDelete", { "customerId": customerId }, inventoryIds);
    }

    // ===============================================================================

    public async customerRequestUpdateInventoriesToReport(requestId: string, inventoryIds: string[]): Promise<DataResult<CustomerRequest>> {
        return await this.fetchPostWithDataResponse("CustomerRequestUpdateInventoriesToReport", { "requestId": requestId }, inventoryIds);
    }

    // ===============================================================================

    public async getAllCustomersAffectedByInventoryChanges(inventoryIds: string[]): Promise<DataResult<string[]>> {
        return await this.fetchPostWithDataResponse("GetAllCustomersAffectedByInventoryChanges", null, { inventoryIds: inventoryIds });
    }

    // ===============================================================================

    public async updateAffectedCustomersAboutInventories(inventoryIds: string[]): Promise<DataResult<string>> {
        return await this.fetchPostWithDataResponse("UpdateAffectedCustomersAboutInventories", null, { inventoryIds: inventoryIds });
    }

    // ===============================================================================

    public async getTaskManagementTasks(): Promise<DataResult<TaskManagementApiResponse>> {
        return await this.fetchPostWithDataResponse("TaskManagementTasksGet", null, null)
    }

    // ===============================================================================

    public async updateTaskManagementTask(task: TaskManagementApiRequest, sendAssignmentEmail: boolean): Promise<DataResult<string>> {
        return await this.fetchPostWithDataResponse("TaskManagementTaskUpdate", null, task);
    }

    // ===============================================================================

    public async deleteTaskManagementTask(taskIds: string[]) {
        return await this.fetchPostWithNoDataResponse("TaskManagementTaskDelete", null, taskIds);
    }

    // ===============================================================================

    public async bulkUpdateTaskManagementTask(tasks: Task[], sendAssignmentEmail: string) {
        return await this.fetchPostWithNoDataResponse("TaskManagementTaskBulkUpdate", { "sendAssignmentEmail": sendAssignmentEmail }, tasks);
    }

    // ===============================================================================

    public async getTaskManagementLinkedInventoriesNames(inventoryIds: string[]): Promise<DataResult<{ [inventoryIds: string]: SimpleInventory }>> {
        return await this.fetchPostWithDataResponse("TaskManagementGetLinkedInventoriesNames", null, inventoryIds);
    }

    // ===============================================================================

    public async getAllowedReportTypesForCurrentUser(onlyAllowBulkReportTypes: boolean): Promise<DataResult<string[]>> {
        return await this.fetchGet("GetAllowedReportTypesForCurrentUser", { "onlyAllowBulkReportTypes": booleanToString(onlyAllowBulkReportTypes) });
    }

    // ===============================================================================

    public async upsellWrite(description: string): Promise<Result> {
        return await this.fetchPostWithNoDataResponse("UpsellWrite", { "description": description }, null);
    }

    // ===============================================================================

    public async inventoriesGetForSupplier(supplierId: string): Promise<DataResult<{ [supplierId: string]: SimpleInventoryWithLinkedMaterialInfo[] }>> {
        return await this.fetchGet("InventoriesGetForSupplier", { "supplierId": supplierId }, ToxnotServiceClient.supplierInventoryFetchTimeoutInMilliseconds);
    }

    // ===============================================================================

    public async inventoriesGetForSupplierIds(supplierIds: string[]): Promise<DataResult<{ [supplierId: string]: SimpleInventoryWithLinkedMaterialInfo[] }>> {
        return await this.fetchPostWithDataResponse("InventoriesGetForSuppliers", null, supplierIds, ToxnotServiceClient.supplierInventoryFetchTimeoutInMilliseconds);
    }

    // ===============================================================================

    public async setSurveyTemplateAsDefault(templateId: string): Promise<Result> {
        return await this.fetchGet("SetDefaultSurveyTemplate", { "templateId": templateId })
    }

    // ===============================================================================

    public async surveyTemplateManagerBundleGet(): Promise<DataResult<SurveyTemplateManagerBundleResponse>> {
        return await this.fetchPostFormWithDataResponse("SurveyTemplateManagerBundleGet", null, null);
    }

    // ===============================================================================

    public async surveyCampaignsGetAll(
        rowsPerPage: number,
        pageIndex: number,
        sortColumnName: string,
        sortAscending: boolean
    ): Promise<DataResult<SurveyCampaignTableResult>> {
        return await this.fetchPostWithDataResponse(
            "SurveyCampaignsGetAll",
            {
                "rowsPerPage": rowsPerPage.toString(),
                "pageIndex": pageIndex.toString(),
                "sortColumnName": sortColumnName,
                "sortAscending": sortAscending ? "True" : "False"
            },
            null);
    }

    // ===============================================================================

    public async surveyCampaignCreate(request: CreateSurveyCampaignRequest): Promise<DataResult<SurveyCampaignTableEntry>> {
        return await this.fetchPostWithDataResponse("SurveyCampaignCreate", null, request);
    }

    public async surveyCampaignUpdate(request: UpdateSurveyCampaignRequest): Promise<DataResult<SurveyCampaignTableEntry>> {
        return await this.fetchPostWithDataResponse("SurveyCampaignUpdate", null, request);
    }

    // ===============================================================================

    public async userUpdateProfileName(name: string): Promise<Result> {
        return await this.fetchPostWithNoDataResponse("UserUpdateProfileName", { "name": name }, null);
    }

    // ===============================================================================

    public async sendCertificationConsolidationSurveyRequest(request: CertificationConsolidationSurveyRequest): Promise<DataResult<CertificationConsolidationSurveyResponse>> {
        return await this.fetchPostWithDataResponse("SendCertificationConsolidationSurveyRequest", null, request);
    }

    // ===============================================================================

    public async getCertificationConsolidationResponse(responseId: string): Promise<DataResult<CertificationConsolidationResponse>> {
        return await this.fetchGet("GetCertificationConsolidationResponse", { "responseId": responseId });
    }

    // ===============================================================================

    public async updateCertificationConsolidationResponse(response: CertificationConsolidationResponse, submit: boolean = false): Promise<DataResult<CertificationConsolidationUpdateResult>> {
        return await this.fetchPostWithDataResponse("UpdateCertificationConsolidationResponse", { "submit": submit ? "True" : "False" }, response);
    }

    // ===============================================================================

    public async getInventory(inventoryId: string): Promise<DataResult<FullInventory>> {
        return await this.fetchGet("GetInventory", { "inventoryId": inventoryId });
    }

    // ===============================================================================

    public async dataCollectionRequestGetInventoryMetadata(inventoryIds: string[]): Promise<DataResult<{ [inventoryId: string]: SurveyDataModel.SurveyInventorySummaryMetadata }>> {
        return await this.fetchPostWithDataResponse("DataCollectionRequestGetInventoryMetadata", null, { inventoryIds: inventoryIds });
    }

    public async getDataCollectionResponse(requestId: string): Promise<DataResult<SurveyDataModel.SurveyResponse>> {
        return this.fetchGet("GetDataCollectionResponse", { "requestId": requestId });
    }

    // ===============================================================================

    public async createLibraryInventory(
        name: string,
        description: string,
        supplier: string,
        plant: string,
        isComponent: boolean
    ): Promise<DataResult<FullInventory>> {
        return await this.fetchPostFormWithDataResponse(
            "CreateLibraryInventory",
            {
                "name": name,
                "description": description,
                "supplier": supplier,
                "plant": plant,
                "isComponent": isComponent.toString()
            },
            null
        );
    }

    // ===============================================================================

    public async libraryInventoriesGetForUserFilteredSortedAndPaged(
        returnMaterials: boolean,
        searchTerms: string[],
        folderId: string | null,
        rowsPerPage: number,
        pageIndex: number
    ): Promise<LibraryInventoriesGetResult | null> {
        const filters: LibraryFiltersModel = {
            activeFilters: []
        };

        if (searchTerms.length > 0) {
            filters.activeFilters.push({
                type: "Search",
                arguments: [],
                values: [searchTerms.join("\n")]
            });
        }

        var data: { [k: string]: string } = {
            "returnMaterials": returnMaterials ? "True" : "False",
            "inventoriesSort": "name",
            "sortAscending": "True",
            "filtersJson": JSON.stringify(filters),
            "rowsPerPage": rowsPerPage.toString(),
            "pageIndex": pageIndex.toString(),
            "includeVersionHistory": "False",
            "restrictResultsToFolderId": "False"
        };

        if (folderId !== null) {
            data["folderId"] = folderId;
            data["restrictResultsToFolderId"] = "True";
        }

        return await this.fetchPostFormWithGenericResponse<LibraryInventoriesGetResult>("LibraryInventoriesGetForUserFilteredSortedAndPaged", data, null);
    }

    // ===============================================================================

    public async clientTeamRequestGet(code: string): Promise<DataResult<ClientTeamRequestGetResult>> {
        return await this.fetchPostFormWithDataResponse("ClientTeamRequestGet", { "code": code }, null);
    }

    // ===============================================================================

    public async dataCollectionImportFileResponse(requestId: string, fileId: string): Promise<DataResult<string>> {
        return this.fetchPostWithDataResponse("DataCollectionImportFileResponse", null, { requestId: requestId, fileId: fileId });
    }

    // ===============================================================================

    public async publicationsDelete(publicInventoryIds: string[]): Promise<Result> {
        return this.fetchPostWithNoDataResponse("PublicationsDelete", null, { publicInventoryIds: publicInventoryIds });
    }

    // ===============================================================================

    public getCoreSurveyResponseData(responseId: string, page: number): Promise<DataResult<CoreSurveyResponseData>> {
        return this.fetchGet("GetCoreSurveyResponseData", { "responseId": responseId, "page": page.toString() });
    }

    // ===============================================================================

    public getCoreSurveyResponseDataForAddedMaterials(responseId: string): Promise<DataResult<CoreSurveyResponseData>> {
        return this.fetchGet("GetAddedMaterialsData", { "responseId": responseId });
    }

    // ===============================================================================

    public getInventoryDetails(responseId: string, inventoryId: string, compact: boolean = false): Promise<DataResult<InventoryDetailsResponse>> {
        return this.fetchGet("GetInventoryDetails", { "responseId": responseId, "inventoryId": inventoryId, ...(compact ? { "compact": "true" } : {}) }, 1000 * 60 * 4);
    }

    // ===============================================================================

    public async scipIuclidStaticDataGet(): Promise<DataResult<ScipData>> {
        return await this.fetchPostWithResponse("ScipIuclidStaticDataGet", null, null);
    }

    public async scipSubmissionUpdate(scipSubmission: SurveyDataModel.ScipSubmissionData, shouldSavePublicInventoryDataDraft: boolean = false): Promise<Result> {
        return this.fetchPostWithNoDataResponse("ScipSubmissionUpdate", null, {
            scipSubmission: scipSubmission,
            shouldSavePublicInventoryDataDraft: shouldSavePublicInventoryDataDraft
        });
    }

    // ===============================================================================

    public async scipSubmissionGet(inventoryId: string, isPassport: boolean, isSurvey: boolean = false): Promise<DataResult<ScipSubmissionDataAll>> {
        return await this.fetchPostWithResponse(
            "ScipSubmissionGet",
            {
                "inventoryId": inventoryId,
                "isPassport": booleanToString(isPassport),
                "isSurvey": booleanToString(isSurvey)
            },
            null
        );
    }

    // ===============================================================================

    // TODO: large-surveys // use paging
    public async validateDataCollectionResponse(responseId: string, inventoryIds: string[], optionalQuestionsInventoryIds: string[] | null = null, validationType: string | null = null): Promise<DataResult<SurveyResponseValidationResult>> {
        return await this.fetchPostWithResponse(
            "ValidateDataCollectionResponse",
            {
                "responseId": responseId
            },
            {
                "inventoryIds": inventoryIds,
                "optionalQuestionsInventoryIds": optionalQuestionsInventoryIds,
                "validationType": validationType
            },
            1000 * 60 * 15,
            false);
    }

    public async invalidateSurveyResponses(responseId: string): Promise<DataResult<SurveyResponseValidationResult>> {
        return await this.fetchPostWithResponse(
            "InValidateSurveyResponses",
            {
                "responseId": responseId
            }, 
            null,
            1000 * 60 * 15,
            false);
    }

    public async validateSupplierQuestionnaire(responseId: string): Promise<DataResult<SurveyResponseValidationResult>> {
        return await this.fetchPostWithResponse(
            "ValidateSupplierQuestionnaire",
            {
                "responseId": responseId,
            },
            null,
            1000 * 60 * 15,
            false);
    }

    // ===============================================================================

    public async inventoryComplianceScores(requestData: InventoryComplianceScoresRequest): Promise<{
        result: ResultType.NotFound | ResultType.NoSeat | ResultType.InsufficientPermissions | ResultType.Failure | ResultType.Forbidden | ResultType.QuotaExceeded | ResultType.NetworkFailure | ResultType.UploadTooLarge;
        data: null;
        success: boolean
    } | {
        result: ResultType | string;
        data: InventoryComplianceScoresResponse;
        success: boolean
    }> {
        return await this.fetchPostWithResponse<any, InventoryComplianceScoresResponse>(
            "InventoryComplianceScores",
            { "inventoryId": requestData.inventoryId, "flagZerosAndMissingPercentagesOnSubstances": requestData.flagZerosAndMissingPercentagesOnSubstances.toString() },
            null,
            1000 * 60 * 15,
            false);

    }

    // ===============================================================================

    public async dataCollectionResponseForward(emailAddress: string, message: string, responseId: string): Promise<Result> {
        return await this.fetchPostWithNoDataResponse("DataCollectionResponseForward", null, { "emailAddress": emailAddress, "message": message, "responseId": responseId });
    }

    // ===============================================================================

    public questionnaireResponseUpdate(responseId: string, inventoryId: string, isAttestationQuestionnaire: boolean, questionnaire: SurveyDataModel.Questionnaire, fileList: FileTableEntry[] | null | undefined): Promise<Result> {
        return this.fetchPostFormWithDataResponse("QuestionnaireResponseUpdate", { "responseId": responseId, "inventoryId": inventoryId, "isAttestationQuestionnaire": isAttestationQuestionnaire.toString(), "questionnaireJson": JSON.stringify(questionnaire), "fileIdInventoryIdAndFileMetadataListJson": JSON.stringify(fileList) }, null, undefined, false);
    }

    // ===============================================================================

    public inventorySummaryDataQuestionnaireResponseUpdate(responseId: string, inventory: InventoryDetailsResponse, questionnaire: SurveyDataModel.Questionnaire): Promise<Result> {
        questionnaire.formatVersion = 4;
        questionnaire.inventoryIdList = [inventory.inventoryId];
        return this.fetchPostFormWithDataResponse("InventorySummaryDataQuestionnaireResponseUpdate", { "responseId": responseId, "inventoryId": inventory.inventoryId, "questionnaireJson": JSON.stringify(questionnaire), "fileIdInventoryIdAndFileMetadataListJson": JSON.stringify(inventory.fileList) }, null, 1000 * 60 * 4, false);
    }

    // ===============================================================================

    public submitDataCollectionResponse(
        responseId: string,
        shouldConvertWeightsToRangesOnPublish: boolean,
        companyIdForPublish: string | null,
        companyNameForPublish: string,
        accessLevel: SharedMaterialsDataModel.SharedMaterialAccessType
    ): Promise<DataResult<SubmitResponseToRequestorOfflineProcessResponse>> {
        const params: { [param: string]: string } = {
            "responseId": responseId,
            "shouldConvertWeightsToRangesOnPublish": booleanToString(shouldConvertWeightsToRangesOnPublish),
            "companyNameForPublish": companyNameForPublish,
            "accessLevel": accessLevel.toString()
        };

        if (companyIdForPublish !== null) {
            params["companyIdForPublish"] = companyIdForPublish;

        }

        return this.fetchPostWithDataResponse("SubmitDataCollectionResponse", params, null);
    }

    // ===============================================================================

    public async dataCollectionResponseInventoryDataUpdateMetadata(responseId: string, inventoryData: SurveyDataModel.SurveyResponseInventoryData) {
        return this.fetchPostWithNoDataResponse(
            "DataCollectionResponseInventoryDataUpdateMetadata",
            null,
            {
                responseId: responseId,
                inventoryData: inventoryData
            }
        )
    }

    // ===============================================================================
    public async updateExtensionRequest(responseId: string, extensionRequest: ExtensionRequestModel, inventoryId: string | null = null): Promise<DataResult<string>> {
        const queryParams: any = {
            "responseId": responseId
        }

        queryParams["inventoryIdToRemoveValidation"] = inventoryId ? inventoryId : null;

        return await this.fetchPostWithDataResponse(
            "UpdateExtensionRequest",
            queryParams,
            extensionRequest
        );
    }

    // ===============================================================================
    public async removeExtensionRequest(extensionRequestId: string, responseOwnerId: string, responseId: string | undefined, inventoryId: string | undefined) {
        const queryParams: any = {

            "extensionRequestId": extensionRequestId,
            "responseOwnerId": responseOwnerId
        }

        queryParams["responseId"] = responseId ? responseId : null;
        queryParams["inventoryId"] = inventoryId ? inventoryId : null;

        return this.fetchPostWithNoDataResponse(
            "RemoveExtensionRequest", queryParams);
    }

    // ===============================================================================

    public getPlausibilityData(inventoryId: string, ownerId: string): Promise<DataResult<PlausibilityData[]>> {
        return this.fetchGet("GetPlausibilityData", { "inventoryId": inventoryId, "ownerId": ownerId });
    }

    // ===============================================================================

    public getObtainmentReports(pageIndex: number, rowsPerPage: number): Promise<DataResult<ObtainmentReportsResult>> {
        return this.fetchGet("ObtainmentReports", { "pageIndex": pageIndex.toString(), "rowsPerPage": rowsPerPage.toString() });
    }
    public async updateUserProfileLanguage(cultureName: string): Promise<DataResult<{ message: string }>> {
        const response = await this.fetchPostWithDataResponse('UpdateUserProfileLanguage', { "cultureName": cultureName.toString() }, null);
        let dataResult = {
            data: response.success ? { message: 'User Language Updated.' } : null,
            result: response.result,
            success: response.success
        };
        return dataResult;
    }

    public async getInventoryStatusDefinations(ownerId: string) {
        const queryParams: any = {
            ownerId
        }

        return await this.fetchGet(
            "GetInventoryStatusDefinations", queryParams);
    }

    public async assignStatusToInventories(inventoryIds: string[], selectedStatus: string, ownerId: string | undefined, isObsolete: boolean) {

        return this.fetchPostWithNoDataResponse(
            "AssignStatusToInventories", null, {
                inventoryIds: inventoryIds,
                ownerId: ownerId,
                status: selectedStatus,
                isObsolete: isObsolete
            });
    }

    // ===============================================================================
    // SFTP Import Configuration Methods
    // ===============================================================================

    /**
     * Gets an SFTP import configuration by its ID.
     */
    public async sftpImportConfigurationGet(id: string): Promise<DataResult<SftpImportConfigurationWorkflow>> {
        const result = await this.fetchPostWithDataResponse<string, SftpImportConfiguration>(
            "SftpImportConfigurationGet", 
            null, 
            id
        );

        // Convert base config to workflow config for UI use
        if (result.success && result.data) {
            const workflowData = convertBackendToWorkflow(result.data);
            return {
                ...result,
                data: workflowData
            };
        }

        return result as DataResult<SftpImportConfigurationWorkflow>;
    }

    // ===============================================================================

    /**
     * Gets all SFTP import configurations for the current user's team owner.
     */
    public async sftpConfigurationsGetAllForAccount(includeInactive: boolean): Promise<DataResult<SftpConfigurationGetByOwnerResponse[]>> {
        return await this.fetchPostWithDataResponse<boolean, SftpConfigurationGetByOwnerResponse[]>(
            "SftpConfigurationsGetAllForAccount", 
            null, 
            includeInactive
        );
    }

    // ===============================================================================

    /**
     * Creates a new SFTP import configuration.
     */
    public async sftpImportConfigurationCreate(model: SftpImportConfigurationWorkflow): Promise<DataResult<any>> {
        // Convert workflow config to backend config
        const backendConfig = convertWorkflowToBackend(model);

        return await this.fetchPostWithDataResponse<SftpImportConfiguration, any>(
            "SftpImportConfigurationCreate", 
            null, 
            backendConfig
        );
    }

    // ===============================================================================

    /**
     * Updates an existing SFTP import configuration.
     */
    public async sftpImportConfigurationUpdate(model: SftpImportConfigurationWorkflow): Promise<Result> {
        // Convert workflow config to backend config
        const backendConfig = convertWorkflowToBackend(model);

        return await this.fetchPostWithNoDataResponse(
            "SftpImportConfigurationUpdate", 
            null, 
            backendConfig
        );
    }

    // ===============================================================================

    /**
     * Deletes an SFTP import configuration.
     */
    public async sftpImportConfigurationDelete(id: string): Promise<Result> {
        return await this.fetchPostWithNoDataResponse(
            "SftpImportConfigurationDelete", 
            null, 
            id
        );
    }

    // ===============================================================================

    /**
     * Sets the active status of an SFTP import configuration.
     */
    public async sftpImportConfigurationSetStatus(model: SftpImportConfigurationSetStatusRequest): Promise<Result> {
        return await this.fetchPostWithNoDataResponse(
            "SftpImportConfigurationSetStatus", 
            null, 
            model
        );
    }

    // ===============================================================================

    /**
     * Gets the recent run history of a configuration.
     */
    public async GetSftpRecentRunHistory(configId: string): Promise<DataResult<any>> {
        return await this.fetchPostWithDataResponse(
            "GetSftpRecentRunHistory", 
            null, 
            configId
        );
    }
    // ===============================================================================
    
    /**
     * Gets the column mapping from a temp file.
     */
    public async SftpExtractSpreadsheetColumns(spreadsheetFile: File, sheetName: string, headerEndRow: number): Promise<any> {
        return await this.fetchPostFormWithGenericResponse(
            "ExtractSpreadsheetColumns", 
            { "sheetName": sheetName, 
                "headerEndRow": headerEndRow.toString() }, 
            { "spreadsheetFile": spreadsheetFile },
        );
    }


    // SFTP Client Configuration Methods
    // ===============================================================================

    /**
     * Gets all SFTP import configurations for the current user's team owner.
     */
    public async sftpClientConfigurationGetByOwner(includeInactive: boolean): Promise<DataResult<any>> {
        const request = { includeInactive: includeInactive };
        return await this.fetchPostWithDataResponse<any, any>(
            "GetConfigurationsByOwner",
            null,
            request
        );
    }

    public async GetNewConfigColumnMappingConflictsDetermination(mappings: InventoryColumnMapping[], configId?: string): Promise<DataResult<any>> {
        const requestData = { mappings, configId };
        return await this.fetchPostWithDataResponse<any, any>(
            "GetNewConfigColumnMappingConflictsDetermination",
            null,
            requestData 
        );
    }

    // ===============================================================================
    // BOM Template APIs
    // ===============================================================================

    /**
     * Get the user's default BOM column template (including extensible columns)
     */
    public async getDefaultBomColumnTemplate(): Promise<DataResult<any>> {
        return await this.fetchGet("DefaultBomColumnTemplateGet", {});
    }

    // ===============================================================================
    // User Settings Get
    // ===============================================================================

    /**
     * Get the user's settings
     */
    public async GetUserSettingsForTeamOwner(): Promise<DataResult<any>> {
        return await this.fetchGet("GetUserSettingsForTeamOwner", {});
    }

    // ===============================================================================
    // Compli API Methods
    // ===============================================================================

    /**
     * Gets the encrypted username from Compli API
     * @param userName The plain text username to encrypt
     * @returns Promise<Result> The result containing success status and encrypted username
     */
    public async getEncryptedUserFromCompli(userName: string): Promise<Result> {
        return await this.fetchGet(
            "GetEncryptedUserFromCompli",
            {
                "userName": userName
            }
        );
    }
}

